import { useState } from 'react';
import { Box, Button, CircularProgress, FormHelperText, Link as MuiLink, Stack, Typography } from '@mui/material';
import { Link } from 'react-router-dom';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import MailOutlined from '@mui/icons-material/MailOutlined';
import MarkEmailReadOutlined from '@mui/icons-material/MarkEmailReadOutlined';
import UserAuthService from '../../../shared/services/user-auth/user-auth.service';
import { AuthLayout } from './auth/auth-layout';
import { AuthField } from './auth/auth-field';
import { useAuthBranding } from './auth/use-auth-branding';
import { AUTH_TOKENS, BRAND_DISPLAY_FONT } from './auth/auth-branding';

export const ForgotPasswordPage = () => {
  const userAuthService = new UserAuthService();
  const branding = useAuthBranding();
  const [sent, setSent] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string>('');

  const forgotPasswordSchema = Yup.object().shape({
    email: Yup.string().email('Email is invalid').required('Email is required'),
  });

  const formik = useFormik({
    initialValues: { email: '' },
    validationSchema: forgotPasswordSchema,
    onSubmit: (values) => {
      setErrorMessage('');
      return userAuthService
        .forgotPassword({ email: values.email.trim().toLowerCase() })
        .then(() => setSent(true))
        .catch((err) => {
          console.log(err);
          setErrorMessage(err?.response?.data?.message || 'Something went wrong. Please try again.');
        });
    },
  });

  if (sent) {
    return (
      <AuthLayout branding={branding}>
        <MarkEmailReadOutlined sx={{ fontSize: 40, color: 'primary.main', mb: '16px' }} />
        <Typography
          component="h1"
          sx={{ mb: '6px', fontSize: '1.6rem', fontFamily: BRAND_DISPLAY_FONT, letterSpacing: '-0.015em', color: 'text.primary' }}
        >
          Check your inbox
        </Typography>
        <Typography sx={{ mb: '26px', fontSize: '0.9375rem', lineHeight: 1.5, color: AUTH_TOKENS.inkSecondary }}>
          We&apos;ve sent a password reset link to <b>{formik.values.email}</b>. Follow the link to set a new password.
        </Typography>
        <MuiLink component={Link} to="/login" underline="none" sx={{ fontSize: '0.875rem', fontWeight: 500, color: 'primary.main' }}>
          Back to sign in
        </MuiLink>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout branding={branding}>
      <Box component="form" onSubmit={formik.handleSubmit} noValidate sx={{ width: '100%' }}>
        <Typography
          component="h1"
          sx={{ mb: '6px', fontSize: '1.6rem', fontFamily: BRAND_DISPLAY_FONT, letterSpacing: '-0.015em', color: 'text.primary' }}
        >
          Reset your password
        </Typography>
        <Typography sx={{ mb: '26px', fontSize: '0.9375rem', lineHeight: 1.5, color: AUTH_TOKENS.inkSecondary }}>
          Enter your email and we&apos;ll send you a link to reset your password.
        </Typography>

        <AuthField
          id="email"
          name="email"
          label="Work email"
          type="email"
          placeholder="you@company.com"
          autoComplete="email"
          icon={<MailOutlined />}
          value={formik.values.email}
          onChange={formik.handleChange}
          onBlur={formik.handleBlur}
          error={formik.touched.email && Boolean(formik.errors.email)}
          helperText={formik.touched.email ? formik.errors.email : undefined}
          focusRing={branding.focusRing}
        />

        {errorMessage && (
          <FormHelperText error role="alert" sx={{ mt: 1.5, ml: '2px', fontSize: '0.8125rem' }}>
            {errorMessage}
          </FormHelperText>
        )}

        <Button
          type="submit"
          fullWidth
          size="large"
          disabled={formik.isSubmitting}
          sx={{ mt: '24px', height: 50, fontSize: '0.9375rem' }}
        >
          {formik.isSubmitting ? <CircularProgress size={20} color="inherit" /> : 'Send reset link'}
        </Button>

        <Typography sx={{ mt: '22px', textAlign: 'center', fontSize: '0.875rem', color: AUTH_TOKENS.inkSecondary }}>
          Remember your password?{' '}
          <MuiLink component={Link} to="/login" underline="none" sx={{ color: 'primary.main', fontWeight: 500 }}>
            Sign in
          </MuiLink>
        </Typography>
      </Box>
    </AuthLayout>
  );
};
